/**
 * One scan on the Scans dashboard (DEC-191 decision 1): the status in words and colour
 * with one explanation sentence, the findings with a small severity bar, AI or Static,
 * and labelled buttons for what can be done in that state. Destructive actions confirm
 * inline, never in a dialog.
 */
import type { PendingScan, ScanStatusResponse, ScanVisibility } from '../../types/api'
import { ranPhrase, shortDuration } from '../../utils/runTime'
import { PillDot, TonePill } from '../shared/Badge'
import { SCAN_STATUS, type ScanStatusKey } from './scanStatus'
import { useConfirm } from './useConfirm'
import {
  SEVERITIES,
  SEVERITY_MARK,
  agoLabel,
  deletedSentence,
  durLabel,
  erasedCount,
  findingsLabel,
  pendingSentence,
  repoLabel,
  scanSentence,
  sourceLabel,
  type SeverityCounts,
  whenLabel,
} from './scanFormat'

/** A row is a scan, or a deferred request that has no scan yet. */
export type DashItem =
  | { kind: 'scan'; id: string; status: ScanStatusKey; at: string; scan: ScanStatusResponse }
  | { kind: 'pending'; id: string; status: ScanStatusKey; at: string; pending: PendingScan }

export interface RowAction {
  label: string
  tone: 'primary' | 'secondary' | 'danger'
  onClick: () => void
  /** Set for a destructive action: it asks first, inline. */
  confirm?: { text: string; yesLabel: string }
  disabled?: boolean
}

const BUTTON_TONE: Record<RowAction['tone'], string> = {
  primary: 'bg-accent text-white hover:bg-[#6A4AAB] active:bg-[#553A8B]',
  secondary: 'border border-hairline-strong text-ink hover:bg-hover-hue',
  danger: 'text-critical hover:bg-chip-critical',
}

export function ActionButton({ action, onClick }: { action: RowAction; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={action.disabled}
      className={`whitespace-nowrap rounded-md px-3 py-[6px] text-[12px] font-semibold transition-colors focus:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50 ${BUTTON_TONE[action.tone]}`}
    >
      {action.label}
    </button>
  )
}

/** The buttons. A destructive one calls `onAsk` instead of acting. */
export function RowActions({
  actions,
  onAsk,
  align = 'end',
}: {
  actions: RowAction[]
  onAsk: (a: RowAction) => void
  align?: 'start' | 'end'
}) {
  return (
    <div className={`flex flex-wrap gap-[6px] ${align === 'end' ? 'md:justify-end' : ''}`}>
      {actions.map((a) => (
        <ActionButton key={a.label} action={a} onClick={() => (a.confirm ? onAsk(a) : a.onClick())} />
      ))}
    </div>
  )
}

/** The inline confirmation of a destructive action (DEC-191: never a dialog). */
export function ConfirmBox({ action, onDone }: { action: RowAction; onDone: () => void }) {
  return (
    <div role="alertdialog" className="flex flex-wrap items-center gap-[10px] rounded-md bg-chip-critical px-3 py-[10px] text-[13px] text-ink">
      <span className="min-w-[200px] flex-1">{action.confirm?.text}</span>
      <button
        type="button"
        onClick={() => { onDone(); action.onClick() }}
        className="whitespace-nowrap rounded-md bg-badge-critical px-3 py-[6px] text-[12px] font-semibold text-on-critical"
      >
        {action.confirm?.yesLabel}
      </button>
      <button
        type="button"
        onClick={onDone}
        className="whitespace-nowrap rounded-md border border-hairline-strong px-3 py-[6px] text-[12px] font-semibold text-body hover:bg-hover-hue"
      >
        Keep it
      </button>
    </div>
  )
}

export function StatusPill({ status }: { status: ScanStatusKey }) {
  const S = SCAN_STATUS[status]
  return (
    <TonePill tone={S.tone} pulse={S.pulse} testId="status-pill">
      {S.word}
    </TonePill>
  )
}

export function StatusDot({ status, size = 8 }: { status: ScanStatusKey; size?: number }) {
  const S = SCAN_STATUS[status]
  return <PillDot tone={S.tone} size={size} pulse={S.pulse} />
}

export function SeverityStrip({ sev, height = 6 }: { sev: SeverityCounts | undefined; height?: number }) {
  const parts = SEVERITIES.filter((k) => (sev?.[k] ?? 0) > 0)
  return (
    <span className="flex overflow-hidden rounded-full bg-fill-subtle" style={{ height, gap: 2 }}>
      {parts.map((k) => (
        <span key={k} className={SEVERITY_MARK[k]} style={{ flex: `${sev![k]} 1 0` }} />
      ))}
    </span>
  )
}

function ModePill({ ai }: { ai: boolean }) {
  return (
    <span
      className={`whitespace-nowrap rounded-full px-[7px] py-[2px] text-[10px] font-semibold ${
        ai ? 'bg-accent/[0.14] text-accent-text' : 'bg-fill-subtle text-faint'
      }`}
    >
      {ai ? 'AI' : 'Static'}
    </span>
  )
}

/** DEC-196: who can see a scan. Absent on an older server: no pill. */
export function VisibilityPill({ visibility }: { visibility: ScanVisibility | null | undefined }) {
  if (!visibility) return null
  return (
    <span
      data-testid="visibility-pill"
      className="whitespace-nowrap rounded-full border border-hairline-strong px-[7px] py-[1px] text-[10px] font-semibold text-muted"
    >
      {visibility === 'private' ? 'Only you' : 'Workspace'}
    </span>
  )
}

function sentenceTone(status: ScanStatusKey): string {
  if (status === 'failed') return 'text-critical'
  if (status === 'partial' || status === 'paused') return 'text-warn'
  return 'text-body'
}

export interface ScanRowProps {
  item: DashItem
  actions: RowAction[]
  /** Highlighted (the bar selected in a repository chart). */
  selected?: boolean
  /** In a repository's list the name is the page's title, so the row shows the branch. */
  showRepo?: boolean
  now?: number
}

export function ScanRow({ item, actions, selected = false, showRepo = true, now = Date.now() }: ScanRowProps) {
  const confirm = useConfirm()
  const status = item.status
  const repoUrl = item.kind === 'scan' ? item.scan.repo_url : item.pending.repo_url
  const branch = item.kind === 'scan' ? item.scan.branch : item.pending.branch
  const ai = item.kind === 'scan' ? item.scan.use_llm : item.pending.use_llm
  // DEC-192: a deleted record says when and by whom, and has no findings left to show.
  const deleted = item.kind === 'scan' && !!item.scan.deleted_at
  const sev = item.kind === 'scan' && !deleted ? item.scan.findings_by_severity : undefined
  const sentence =
    item.kind === 'pending'
      ? pendingSentence(status, item.pending)
      : deleted
        ? deletedSentence(item.scan)
        : scanSentence(status, item.scan)
  const erased = item.kind === 'scan' && deleted ? erasedCount(item.scan) : 0

  // DEC-195: run time, never wall-clock time that counts pauses.
  let dur = ''
  if (item.kind === 'pending') dur = `requested ${agoLabel(item.pending.requested_at, now)}`
  else if (status === 'running')
    dur =
      typeof item.scan.run_seconds === 'number'
        ? `running ${shortDuration(item.scan.run_seconds)}`
        : `running for ${durLabel(item.scan.started_at, now)}`
  else if (status === 'paused') dur = 'paused'
  else if (status === 'queued') dur = `waiting ${durLabel(item.scan.started_at, now)}`
  else if (item.scan.completed_at) dur = ranPhrase(item.scan)

  return (
    <div
      data-testid="scan-row"
      data-scan-id={item.id}
      className={`flex flex-col gap-2 border-b border-hairline px-[18px] py-3 last:border-b-0 ${
        selected ? 'bg-fill-subtle shadow-[inset_3px_0_0_rgb(var(--accent))]' : status === 'running' ? 'bg-accent/[0.06]' : ''
      }`}
    >
      {/* Without the repository name (the repository view), the row sits in a narrower panel:
          the findings move under the date line so the middle column keeps its width
          (founder, 2026-10-12: the old four-column row squeezed the date into a sliver). */}
      <div
        className={`grid items-center gap-[14px] ${
          showRepo
            ? 'md:grid-cols-[170px_minmax(0,1fr)_210px_auto]'
            : 'md:grid-cols-[150px_minmax(0,1fr)_auto]'
        }`}
      >
        <StatusPill status={status} />
        <div className="flex min-w-0 flex-col gap-[3px]">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-[2px]">
            {showRepo && <span className="text-[14px] font-semibold text-ink">{repoLabel(repoUrl)}</span>}
            <span className="whitespace-nowrap text-[12px] text-muted">{sourceLabel(repoUrl, branch)}</span>
            <span className="whitespace-nowrap text-[13px] text-body">{whenLabel(item.at, now)}</span>
            <span className="whitespace-nowrap text-[12px] text-faint">{agoLabel(item.at, now)}</span>
            {dur && <span data-testid="row-run-time" className="whitespace-nowrap text-[12px] text-faint">· {dur}</span>}
            <ModePill ai={ai} />
            {item.kind === 'scan' && <VisibilityPill visibility={item.scan.visibility} />}
            {item.kind === 'scan' && item.scan.pushed_by && (
              <span className="whitespace-nowrap text-[12px] text-muted">Pushed by @{item.scan.pushed_by}</span>
            )}
          </div>
          {sentence && (
            <span
              data-testid={deleted ? 'deleted-note' : undefined}
              className={`text-[13px] leading-[1.45] ${deleted ? 'text-body' : sentenceTone(status)}`}
            >
              {sentence}
            </span>
          )}
          {!showRepo && <FindingsCell deleted={deleted} erased={erased} status={status} sev={sev} compact />}
        </div>
        {showRepo && <FindingsCell deleted={deleted} erased={erased} status={status} sev={sev} />}
        <RowActions actions={actions} onAsk={confirm.ask} />
      </div>
      {confirm.box}
    </div>
  )
}

/** Findings total and the severity strip; under the date line in the compact row. */
function FindingsCell({
  deleted,
  erased,
  status,
  sev,
  compact = false,
}: {
  deleted: boolean
  erased: number
  status: Parameters<typeof findingsLabel>[0]
  sev: Parameters<typeof findingsLabel>[1]
  compact?: boolean
}) {
  return (
        <div className={`flex flex-col gap-[5px] ${compact ? 'mt-[6px] max-w-[260px]' : ''}`}>
          {deleted ? (
            <span className="whitespace-nowrap text-[13px] text-muted">
              {erased} {erased === 1 ? 'finding' : 'findings'} erased · no results to open
            </span>
          ) : (
            <>
              <span className="whitespace-nowrap text-[13px] text-body">{findingsLabel(status, sev)}</span>
              <SeverityStrip sev={sev} />
            </>
          )}
        </div>
  )
}
